import { applyColumnMappings, dropExcludedColumns } from './column-mapping';
import { applyTransformations } from './transformations';
import type { ColumnMapping, ImportRow, Transformation } from './types';

const LEGACY_CORE_FIELDS = ['employeeId', 'languageId', 'role'] as const;
type LegacyCoreField = (typeof LEGACY_CORE_FIELDS)[number];

const LEGACY_KEY_FOR_CORE_FIELD: Record<LegacyCoreField, string> = {
  employeeId: 'employee id',
  languageId: 'language',
  role: '$survey_role',
};

function isLegacyCoreField(value: unknown): value is LegacyCoreField {
  return LEGACY_CORE_FIELDS.includes(value as LegacyCoreField);
}

/**
 * Which of `employeeId`/`languageId`/`role` some column mapping or transformation actually
 * targets for this company. `role`, unlike the other two, is also a plausible raw HRIS header
 * text -- a customer's own literal "Role" column lowercases to the same string this core field's
 * key already uses, so `withLegacyCoreFieldKeys` needs this set to tell the two apart.
 */
function configuredLegacyCoreFields(
  columnMappings: ColumnMapping[],
  transformations: Transformation[],
): Set<LegacyCoreField> {
  const configured = new Set<LegacyCoreField>();
  for (const target of [...columnMappings, ...transformations]) {
    if (isLegacyCoreField(target.targetCoreField)) {
      configured.add(target.targetCoreField);
    }
  }
  return configured;
}

/** Renames a core field to the legacy key the import thunks match on, only for a configured target -- otherwise a company's own raw "Role" column could be mistaken for one. */
function withLegacyCoreFieldKeys(row: ImportRow, configuredCoreFields: ReadonlySet<LegacyCoreField>): ImportRow {
  const result: ImportRow = {};
  for (const [key, value] of Object.entries(row)) {
    if (isLegacyCoreField(key) && configuredCoreFields.has(key)) {
      result[LEGACY_KEY_FOR_CORE_FIELD[key]] = value;
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Layers a batch of transformation results onto the already-mapped row. A transformation's result
 * overwrites whatever column mapping (or the raw header) produced for that same target key --
 * except when the transformation itself computed null for a key the mapped row already had a
 * value at, which is left untouched instead of clobbered. Nothing configured today has both a
 * mapping and a transformation target the same key, but if it ever does, a transformation with no
 * applicable input for one particular row (e.g. FIRST_NON_NULL with every candidate empty)
 * shouldn't silently erase a real value the file supplied through the other path -- especially on
 * an update import, where that erasure would delete existing data.
 */
function mergeTransformationResults(mappedRow: ImportRow, transformedRow: Record<string, string | null>): ImportRow {
  const merged = { ...mappedRow };
  for (const [key, value] of Object.entries(transformedRow)) {
    if (value !== null || !(key in mappedRow)) {
      merged[key] = value;
    }
  }
  return merged;
}

/**
 * Runs a raw parsed row through column mapping, then custom transformations computed from the
 * same post-mapping row (matching how the known backfilled transformations were authored -- see
 * the seed migration's own doc comment), then drops any excluded columns, then the legacy
 * core-field bridge. Exclusion runs after transformations, not as part of column mapping, since
 * an excluded column can still be a transformation input (e.g. Harris' Date of Birth feeds its
 * Generation transformation even though the raw value itself must never be persisted).
 *
 * `row`'s keys must already be lowercased by the caller (matching `applyColumnMappings`'
 * case-insensitive matching contract).
 */
export function applyMappingsAndTransformations(
  columnMappings: ColumnMapping[],
  transformations: Transformation[],
  row: ImportRow,
): ImportRow {
  const mappedRow = applyColumnMappings(columnMappings, row);
  const merged = mergeTransformationResults(mappedRow, applyTransformations(transformations, mappedRow));
  const withExclusionsDropped = dropExcludedColumns(columnMappings, merged);
  return withLegacyCoreFieldKeys(withExclusionsDropped, configuredLegacyCoreFields(columnMappings, transformations));
}
